import { useEffect, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';

import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareIcon from '@mui/icons-material/Share';
import { getProducto } from './catalogApi';
import { CatalogDetalleSkeleton } from './CatalogSkeletons';
import { copyCatalogLink } from './catalogShare';
import CatalogImageGallery from './CatalogImageGallery';
import { getProductImageUrls } from '../retail-pos/utils/productImages';
import CatalogStockLabel from './CatalogStockLabel';
import CatalogInterestStar from './CatalogInterestStar';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildWhatsAppUrl = (whatsapp, producto, tienda, currentUrl) => {
  if (!whatsapp) return null;
  const phone = whatsapp.replace(/\D/g, '');
  const text  = encodeURIComponent(
    `Hola, me interesa este producto:\n` +
    `*${producto.nombre}*\n` +
    (producto.marca ? `Marca: ${producto.marca}\n` : '') +
    (producto.talla ? `Talla: ${producto.talla}  ` : '') +
    (producto.color ? `Color: ${producto.color}\n` : '\n') +
    `Precio: ${tienda.moneda} ${Number(producto.precio_venta).toFixed(2)}\n` +
    currentUrl
  );
  return `https://wa.me/${phone}?text=${text}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Mobile-first bottom sheet (Modal + Slide) for product detail.
 *
 * Props:
 *  slug        {string}   – store slug (for API call + share URL)
 *  productId   {string}   – UUID of the product; null → drawer closed
 *  tiendaFull  {object}   – { nombre, moneda, whatsapp } from catalog response
 *  onClose     {function}
 *  initialData {object?}  – optimistic product data from the grid card
 */
export default function CatalogProductoDetalle({
  slug,
  productId,
  tiendaFull,
  onClose,
  initialData,
  interestActive = false,
  onInterestClick,
  interestJustSaved = false,
}) {
  const open = !!productId;

  const [producto, setProducto] = useState(initialData || null);
  const [loading, setLoading]   = useState(false);
  const [shareNotice, setShareNotice] = useState('');

  // Fetch full detail when productId changes
  useEffect(() => {
    if (!productId || !slug) return;

    // Reset state
    if (initialData) setProducto(initialData);

    const controller = new AbortController();
    setLoading(true);

    getProducto(slug, productId, controller.signal)
      .then((data) => {
        if (data?.ok && data.producto) setProducto(data.producto);
      })
      .catch(() => {/* abort or network error — show initialData */})
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [slug, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleShare = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const url = `${window.location.origin}/store/${slug}/p/${productId}`;
    const result = await copyCatalogLink(url);
    setShareNotice(result.message || (result.ok ? 'Enlace copiado' : 'No se pudo copiar el enlace'));
  }, [slug, productId]);

  const tienda     = tiendaFull || {};
  const moneda     = tienda.moneda || 'BOB';
  const showImages = tienda.fotosPremiumActivo !== false;
  const productUrl = `${window.location.origin}/store/${slug}/p/${productId}`;
  const waUrl      = producto && buildWhatsAppUrl(tienda.whatsapp, producto, tienda, productUrl);

  if (!open) {
    return (
      <Snackbar
        open={Boolean(shareNotice)}
        autoHideDuration={2800}
        onClose={() => setShareNotice('')}
        message={shareNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    );
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        disableScrollLock
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={producto?.nombre ? `Detalle: ${producto.nombre}` : 'Detalle del producto'}
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '100%',
            maxHeight: '92dvh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            outline: 'none',
            pb: 'max(12px, env(safe-area-inset-bottom))',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
          }}
        >
      {/* Header: handle + close (always visible above content) */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 1.5,
          pb: 0.5,
          px: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
        <IconButton
          onClick={onClose}
          aria-label="Cerrar"
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            bgcolor: 'grey.100',
            boxShadow: 1,
            '&:hover': { bgcolor: 'grey.200' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      {loading && !producto ? (
        <CatalogDetalleSkeleton />
      ) : producto ? (
          <Box>
            <Box sx={{ position: 'relative' }}>
              <CatalogImageGallery
                urls={showImages ? getProductImageUrls(producto) : []}
                alt={producto.nombre}
                fixedHeight={{ xs: 'min(52vh, 420px)', sm: 360 }}
                showImages={showImages}
              />
              <CatalogInterestStar
                active={interestActive}
                onClick={onInterestClick}
                justSaved={interestJustSaved}
                size="medium"
                sx={{ position: 'absolute', top: 12, right: 12 }}
              />
            </Box>

            <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
              {/* Category / brand chip */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {producto.categoria && (
                  <Chip label={producto.categoria} size="small" sx={{ borderRadius: 1 }} />
                )}
                {producto.marca && (
                  <Chip label={producto.marca} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
                )}
              </Box>

              <Typography variant="h6" fontWeight={700} lineHeight={1.2} sx={{ mb: 0.5 }}>
                {producto.nombre}
              </Typography>

              {/* Attributes row */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5, mb: 1.5 }}>
                {producto.talla && (
                  <Typography variant="body2" color="text.secondary">
                    Talla: <b>{producto.talla}</b>
                  </Typography>
                )}
                {producto.color && (
                  <Typography variant="body2" color="text.secondary">
                    Color: <b>{producto.color}</b>
                  </Typography>
                )}
                {producto.corte && (
                  <Typography variant="body2" color="text.secondary">
                    Corte: <b>{producto.corte}</b>
                  </Typography>
                )}
              </Box>

              {/* Price + stock */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Typography variant="h5" fontWeight={800} color="primary">
                  {moneda} {Number(producto.precio_venta || 0).toFixed(2)}
                </Typography>
                <CatalogStockLabel producto={producto} />
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* CTAs */}
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: 'column' }}>
                {waUrl && (
                  <Button
                    component="a"
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      bgcolor: '#25D366',
                      '&:hover': { bgcolor: '#1ebe59' },
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  >
                    Pedir por WhatsApp
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                  sx={{ borderRadius: 3, py: 1.2 }}
                >
                  Compartir producto
                </Button>
              </Box>

            </Box>
          </Box>
      ) : null}
        </Box>
      </Modal>

      <Snackbar
        open={Boolean(shareNotice)}
        autoHideDuration={2800}
        onClose={() => setShareNotice('')}
        message={shareNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
